/**
 * Endpoint Mapping for API Versioning
 * 
 * This file maps endpoints between:
 * - v1: Original blob-storage based endpoints
 * - db: Current database endpoints with /api/v1/db/ prefix
 * - v2: Future consolidated API endpoints (currently mapped to db endpoints)
 */

export interface EndpointMap {
  v1: string;
  db: string;
  v2: string; // Future endpoint
}

// Client endpoints
export const CLIENT_ENDPOINTS = {
  LIST_CLIENTS: {
    v1: '/api/v1/business-config/clients',
    db: '/api/v1/db/clients',
    v2: '/api/v2/clients'
  },
  GET_CLIENT: {
    v1: '/api/v1/business-config/client/{id}',
    db: '/api/v1/db/client/{id}',
    v2: '/api/v2/clients/{id}'
  },
  CREATE_CLIENT: {
    v1: '/api/v1/business-config/client',
    db: '/api/v1/db/client',
    v2: '/api/v2/clients'
  }
};

// Workflow endpoints
export const WORKFLOW_ENDPOINTS = {
  LIST_WORKFLOWS: {
    v1: '/api/v1/business-config/workflows',
    db: '/api/v1/db/client/{client_id}/workflows',
    v2: '/api/v2/clients/{client_id}/workflows'
  },
  GET_WORKFLOW: {
    v1: '/api/v1/business-config/{id}',
    db: '/api/v1/db/workflow/{id}',
    v2: '/api/v2/workflows/{id}'
  },
  CREATE_WORKFLOW: {
    v1: '/api/v1/business-config/',
    db: '/api/v1/db/client/{client_id}/workflow',
    v2: '/api/v2/workflows'
  }
};

// Module 1 - Keyword Analysis
export const MODULE1_ENDPOINTS = {
  START_ANALYSIS: {
    v1: '/api/v1/analyze',
    db: '/api/v1/db/workflow/{workflow_id}/analyze',
    v2: '/api/v2/workflows/{workflow_id}/analyze'
  },
  GET_ANALYSIS_STATUS: {
    v1: '/api/v1/analyze/{workflow_id}',
    db: '/api/v1/db/workflow/{workflow_id}/status',
    v2: '/api/v2/workflows/{workflow_id}/analysis/status'
  },
  GET_ANALYSIS_RESULTS: {
    v1: '/api/v1/workflows/{workflow_id}/results',
    db: '/api/v1/db/workflow/{workflow_id}/results',
    v2: '/api/v2/workflows/{workflow_id}/analysis/results'
  },
  VERIFY_ANALYSIS: {
    v1: '/api/v1/verify',
    db: '/api/v1/db/workflow/{workflow_id}/verify',
    v2: '/api/v2/workflows/{workflow_id}/analysis/verify'
  }
};

// Module 2 - Code Generation
export const MODULE2_ENDPOINTS = {
  GENERATE_CODE: {
    v1: '/api/v1/module2/generate',
    db: '/api/v1/db/workflow/{workflow_id}/generate',
    v2: '/api/v2/workflows/{workflow_id}/code/generate'
  },
  GET_CODE_STATUS: {
    v1: '/api/v1/module2/{workflow_id}/status',
    db: '/api/v1/db/workflow/{workflow_id}/status',
    v2: '/api/v2/workflows/{workflow_id}/code/status'
  },
  GET_CODE: {
    v1: '/api/v1/module2/{workflow_id}/code',
    db: '/api/v1/db/workflow/{workflow_id}/modules/{module_id}/code',
    v2: '/api/v2/workflows/{workflow_id}/code'
  },
  EXECUTE_CODE: {
    v1: '/api/v1/module2/{workflow_id}/execute',
    db: '/api/v1/db/workflow/{workflow_id}/modules/{module_id}/execute',
    v2: '/api/v2/workflows/{workflow_id}/code/execute'
  }
};

// Module 3 - Semantic Analysis
export const MODULE3_ENDPOINTS = {
  START_SEMANTIC_ANALYSIS: {
    v1: '/api/v1/module3/analyze',
    db: '/api/v1/db/workflow/{workflow_id}/semantic',
    v2: '/api/v2/workflows/{workflow_id}/semantic/analyze'
  },
  GET_SEMANTIC_STATUS: {
    v1: '/api/v1/module3/{workflow_id}/status',
    db: '/api/v1/db/workflow/{workflow_id}/semantic/status',
    v2: '/api/v2/workflows/{workflow_id}/semantic/status'
  },
  GET_FRAMEWORK: {
    v1: '/api/v1/module3/{workflow_id}/framework',
    db: '/api/v1/db/workflow/{workflow_id}/framework',
    v2: '/api/v2/workflows/{workflow_id}/semantic/framework'
  }
};

// Module 4 - Batch Processing
export const MODULE4_ENDPOINTS = {
  LIST_CONFIGS: {
    v1: '/api/v1/module4/configs',
    db: '/api/v1/db/client/{client_id}/configs',
    v2: '/api/v2/clients/{client_id}/batch/configs'
  },
  GET_CONFIG: {
    v1: '/api/v1/module4/config/{id}',
    db: '/api/v1/db/config/{id}',
    v2: '/api/v2/batch/configs/{id}'
  },
  START_BATCH: {
    v1: '/api/v1/module4/batch',
    db: '/api/v1/db/workflow/{workflow_id}/batch',
    v2: '/api/v2/workflows/{workflow_id}/batch/start'
  },
  GET_BATCH_STATUS: {
    v1: '/api/v1/module4/batch/{id}/status',
    db: '/api/v1/db/module/{module_id}/status',
    v2: '/api/v2/batch/{id}/status'
  }
};

// Helper function to replace path parameters
export function replaceParams(endpoint: string, params: Record<string, string | number>): string {
  let result = endpoint;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`{${key}}`, String(value));
  });
  return result;
}

// Helper function to get the correct endpoint based on API version
export function getEndpoint(
  endpointMap: EndpointMap, 
  version: 'v1' | 'db' | 'v2', 
  params: Record<string, string | number> = {}
): string {
  const endpoint = endpointMap[version];
  return replaceParams(endpoint, params);
} 